import useEmblaCarousel from 'embla-carousel-react';
import { useCallback, useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { trackEvent, ANALYTICS_EVENTS } from '@/lib/analytics';
import { CarouselNav, CarouselDots, TopicCard } from '@/components/ds';
import { useTopics } from '@/hooks/useTopics';
import { useArticlesGroupedByTopic } from '@/hooks/useArticlesByTopic';
interface TopicCardsCarouselProps {
  /** Currently selected topic name (or "All") */
  activeTopic: string;
  /** Handler when a topic is clicked */
  onTopicChange: (topic: string) => void;
  /** Additional className */
  className?: string;
}

/**
 * Horizontally scrolling carousel of topic cards for the Learn page.
 * Displays all active topics as large pill buttons.
 * Clicking a topic filters the page content.
 * 
 * Features:
 * - Standard header above carousel (matching other sections)
 * - Platinum background surface with proper section spacing
 * - Mobile: pagination dots; Desktop: arrow navigation
 */
export default function TopicCardsCarousel({
  activeTopic,
  onTopicChange,
  className
}: TopicCardsCarouselProps) {
  const {
    data: topics,
    isLoading: topicsLoading
  } = useTopics();
  const {
    data: articlesGrouped,
    isLoading: articlesLoading
  } = useArticlesGroupedByTopic();
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    containScroll: 'trimSnaps',
    slidesToScroll: 1,
    dragFree: true
  });
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [showNavButtons, setShowNavButtons] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);
  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);
  const scrollTo = useCallback((index: number) => {
    if (emblaApi) emblaApi.scrollTo(index);
  }, [emblaApi]);
  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);
  useEffect(() => {
    if (!emblaApi) return;
    const checkNavVisibility = () => {
      const scrollSnaps = emblaApi.scrollSnapList();
      setShowNavButtons(scrollSnaps.length > 1);
    };
    onSelect();
    checkNavVisibility();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', () => {
      onSelect();
      checkNavVisibility();
    });
    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi, onSelect]);

  // Filter to only active topics that have at least one published article
  const topicsWithArticles = (topics?.filter(t => t.is_active) || []).filter(topic => {
    const articles = articlesGrouped?.[topic.id];
    return articles && articles.length > 0;
  });
  const isLoading = topicsLoading || articlesLoading;
  if (isLoading || topicsWithArticles.length === 0) {
    return null;
  }
  return <section className={cn("bg-platinum py-12 md:py-16 lg:py-20 overflow-hidden", className)}>
      {/* Section header with inline nav on desktop */}
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 mb-6 md:mb-8">
        <div className="flex items-end justify-between">
          <h2 className="text-32 md:text-56 font-semibold text-foreground">Explore</h2>
          
          {/* Desktop: Arrows inline with header */}
          {showNavButtons && <div className="hidden md:block">
              <CarouselNav onPrev={scrollPrev} onNext={scrollNext} canScrollPrev={canScrollPrev} canScrollNext={canScrollNext} variant="subdued" />
            </div>}
        </div>
      </div>

      {/* Scrollable pills container - standard carousel behavior */}
      <div className="pl-4 md:pl-8 lg:pl-[max(2rem,calc((100vw-1400px)/2+2rem))]">
        {/* Important: overflow-visible here prevents pills being clipped at the inner content margin.
            The section handles clipping at the viewport edge via overflow-hidden. */}
        <div ref={emblaRef} className="overflow-visible">
          <div className="flex gap-2 md:gap-3 embla-gesture-lock">
            {topicsWithArticles.map((topic, index) => <div key={topic.id} className={cn("flex-shrink-0", index === topicsWithArticles.length - 1 && "mr-4 md:mr-8 lg:mr-[max(2rem,calc((100vw-1400px)/2+2rem))]")}>
                <TopicCard name={topic.name} color={topic.tag_color} iconSvg={topic.icon_svg} isActive={activeTopic === topic.name} onClick={() => {
                  // ⚠️ ANALYTICS: Track topic selection from carousel card
                  trackEvent(ANALYTICS_EVENTS.TOPIC_SELECTED, {
                    topic_name: topic.name,
                    topic_id: topic.id || topic.name.toLowerCase(),
                    selection_source: 'learn_page',
                  });
                  onTopicChange(topic.name);
                }} />
              </div>)}
          </div>
        </div>
      </div>

      {/* No pagination dots for topic pills - they scroll freely */}
    </section>;
}